import { DialogElement } from 'theme';

const REQUEST_TIMEOUT = 5000;

function safeLink(value) {
  if (typeof value !== 'string' || /[\s\\\u0000-\u001f]/.test(value)) return null;
  if (!(value.startsWith('/') && !value.startsWith('//')) && !value.startsWith('https://')) return null;
  try {
    const url = new URL(value, window.location.origin);
    return url.username || url.password ? null : url.href;
  } catch {
    return null;
  }
}

// Where a popup link leads, for analytics: its path, or host and path when it leaves the store.
function destination(href) {
  const url = new URL(href);
  return (url.host === window.location.host ? url.pathname : `${url.host}${url.pathname}`).slice(0, 300);
}

const secondsSince = (start) => Math.min(86400, Math.round((performance.now() - start) / 100) / 10);

// Why the popup could not be shown, sent as the banner.failed reason.
class PopupFailure extends Error {
  constructor(reason, cause) {
    super(`Campaign popup failed: ${reason}`, { cause });
    this.reason = reason;
  }
}

class StreamerCampaignPopup extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    const signal = this._abortController.signal;
    const listen = (element, event, handler, options) => element?.addEventListener(event, handler, { ...options, signal });
    this._shareElement = this.querySelector('[data-streamer-popup-share]');
    this._shareToggle = this.querySelector('[data-streamer-popup-share-toggle]');
    this._shareActions = this.querySelector('[data-streamer-popup-share-actions]');
    listen(this.querySelector('[data-streamer-popup-backdrop]'), 'click', () => {
      this._endView('backdrop');
      this.hide();
    });
    // The close button and Escape close the popup through DialogElement, which calls hide() right after these.
    listen(this, 'dialog:force-close', () => this._closingWith('close_button'), { capture: true });
    listen(document, 'keydown', (event) => {
      if (event.key === 'Escape') this._closingWith('escape');
    });
    listen(this._shareToggle, 'click', () => this._setShareMenu(!this._shareElement.hasAttribute('data-expanded')));
    listen(this._shareActions, 'click', (event) => {
      const button = event.target.closest('[data-streamer-popup-share-action]');
      if (!button || button.hidden || button.disabled) return;
      const action = button.dataset.streamerPopupShareAction;
      this._sharing?.act(action).then((outcome) => outcome && this.track('banner.shared', { action, outcome }));
    });
    listen(document, 'pointerdown', (event) => {
      if (!this._shareElement?.contains(event.target)) this._setShareMenu(false);
    });
    listen(this, 'keydown', (event) => {
      if (event.key === 'Escape' && this._shareElement?.hasAttribute('data-expanded')) {
        event.stopPropagation();
        this._setShareMenu(false);
      }
    });
    const comparison = this.querySelector('[data-streamer-comparison-input]');
    listen(comparison, 'input', () => {
      this._setComparisonPosition(comparison.value);
      if (!this._view || this._view.sliderUsed) return;
      this._view.sliderUsed = true;
      this.track('banner.slider_used', { experience_id: this._sliderExperienceId });
    });
    this._initialize(signal).catch((error) => {
      if (signal.aborted) return;
      this._trackFailure(error);
      this._reportError(error);
    });
  }

  get shouldLock() { return true; }

  // Sends a banner.* event with the popup's campaign, creator and layout through madmonq-analytics.js (loaded by
  // the layout); without it nothing is tracked.
  track(name, properties = {}) {
    if (this._banner?.layout) window.MadmonqAnalytics?.track(name, { ...this._banner, ...properties });
  }

  hide() {
    this._endView(this._closeMethod || 'other');
    this._setShareMenu(false);
    this._reactions?.stop();
    this._poll?.stop();
    this._giveaway?.stop();
    return super.hide();
  }

  async _initialize(signal) {
    const started = performance.now();
    const referralParameter = this.dataset.referralParameter || 'ref';
    const referralCode = new URL(window.location.href).searchParams.get(referralParameter)?.trim();
    if (!referralCode || referralCode.length > 255) return;
    this._banner = { creator: referralCode };
    const [{ CampaignImages }, { CampaignReactions }, { CampaignPoll }, { CampaignHype }, { CampaignSharing }, participation] = await Promise.all([
      import(this.dataset.imageModuleUrl), import(this.dataset.reactionModuleUrl),
      import(this.dataset.pollModuleUrl), import(this.dataset.hypeModuleUrl), import(this.dataset.sharingModuleUrl),
      import(this.dataset.participationModuleUrl)
    ]);
    if (signal.aborted) return;
    const endpoint = new URL(this.dataset.resolverUrl || '/apps/madmonq-campaigns/resolve/', window.location.origin);
    if (endpoint.origin !== window.location.origin || endpoint.username || endpoint.password) {
      throw new Error('The campaign resolver must use a same-store app proxy path.');
    }
    endpoint.search = '';
    endpoint.hash = '';
    endpoint.searchParams.set('ref', referralCode);
    let payload;
    const resolve = async () => {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timeout = window.setTimeout(abort, REQUEST_TIMEOUT);
      try {
        const response = await fetch(endpoint, {
          headers: { Accept: 'application/json' }, credentials: 'same-origin',
          cache: 'no-store', redirect: 'error', signal: controller.signal
        });
        if (!response.ok) throw new Error(`Campaign resolution failed (${response.status}).`);
        payload = await response.json();
        try { localStorage.removeItem('madmonq:campaign-visitor'); } catch {}
      } finally {
        window.clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
      }
    };
    await resolve().catch((error) => {
      // The request is only aborted by its timeout here; a disconnected popup reports nothing.
      throw new PopupFailure(error.name === 'AbortError' ? 'timeout' : 'resolve_failed', error);
    });
    if (signal.aborted || !payload?.campaign || !payload.creator) return;
    const campaign = payload.campaign;
    this._banner = { campaign_id: campaign.id, creator: payload.creator.referral_code };
    if (!campaign.id || !campaign.content?.heading) throw new PopupFailure('render_failed');
    this._images = new CampaignImages(payload.images || {}, signal);
    this._Sharing = CampaignSharing;
    const visibleImages = this._renderCampaign(campaign, payload.creator, payload.placements);
    // The experiences change data-experience while they run (voted, hype); analytics keeps the layout as rendered.
    this._banner.layout = this.dataset.experience;
    if (!visibleImages.length) throw new PopupFailure('image_failed');
    await this._waitForImages(visibleImages, signal).catch((error) => {
      throw new PopupFailure(error.name === 'TimeoutError' ? 'timeout' : 'image_failed', error);
    });
    if (signal.aborted) return;
    document.querySelector('newsletter-popup')?.remove();
    this._reactions = new CampaignReactions(this, payload, endpoint, signal, CampaignHype, participation);
    this._poll = new CampaignPoll(this, payload, endpoint, signal, participation);
    if (payload.placements.some((placement) => placement.experience.experience_type === 'giveaway')) {
      const { CampaignGiveaway } = await import(this.dataset.giveawayModuleUrl);
      if (signal.aborted) return;
      this._giveaway = new CampaignGiveaway(this, payload, endpoint, signal);
    }
    const experiences = [...new Set(payload.placements.map((placement) => placement.experience.experience_type))];
    this._view = { start: performance.now() };
    await this.show();
    this.track('banner.viewed', {
      experiences: experiences.sort().join(',').slice(0, 120),
      load_ms: Math.min(600000, Math.round(performance.now() - started))
    });
  }

  _renderCampaign(campaign, creator, placements) {
    const content = campaign.content;
    const text = (selector, value) => {
      const element = this.querySelector(selector);
      if (element) {
        element.textContent = value || '';
        element.hidden = !value;
      }
    };
    text('[data-streamer-popup-headline-main]', content.heading);
    text('[data-streamer-popup-headline-accent]', content.heading_accent);
    text('[data-streamer-popup-description]', content.description);
    text('[data-streamer-popup-domain]', `${(this.dataset.storefrontDomain || window.location.hostname).replace(/^www\./i, '')}/?${encodeURIComponent(this.dataset.referralParameter || 'ref')}=`);
    text('[data-streamer-popup-referral]', encodeURIComponent(creator.referral_code));
    // Heading size is the desktop size in pixels; every screen size scales by the same ratio.
    if (Number.isInteger(content.heading_size)) {
      this.style.setProperty('--streamer-popup-heading-scale', String(content.heading_size / 68));
    }
    if (/^#[0-9a-f]{6}$/i.test(content.accent_color || '')) {
      this.style.setProperty('--streamer-popup-accent', content.accent_color);
      this.style.setProperty('--streamer-popup-accent-rgb', content.accent_color.match(/[0-9a-f]{2}/gi).map((v) => parseInt(v, 16)).join(' '));
    }
    const experiences = placements.map((placement) => placement.experience);
    const experience = (type) => experiences.find((item) => item.experience_type === type);
    const configuration = (type) => experience(type)?.configuration;
    const promo = configuration('promo');
    const slider = configuration('image_switcher');
    const sharing = campaign.sharing?.is_enabled;
    const giveaway = configuration('giveaway');
    this.dataset.experience = giveaway ? 'giveaway' : configuration('poll') ? 'voting' : promo ? 'promo' : slider ? 'before-after' : 'default';
    const images = [];
    const mount = (selector, placement, options) => {
      const image = this._images.mount(this.querySelector(selector), placement, options);
      if (image) images.push(image);
      return image;
    };
    const background = content.background_image;
    if (!this._images.images[background?.shopify_file_gid]?.src) return [];
    if (!slider) mount('[data-streamer-popup-poster]', background, { alt: this._images.images[background.shopify_file_gid].alt || '' });
    const before = slider?.slides?.[0]?.image || background;
    const after = slider?.slides?.[1]?.image || background;
    mount('[data-streamer-popup-poster-ambient]', slider ? before : background);
    this._renderCTA(content, mount);
    if (giveaway) {
      text('[data-streamer-giveaway-title]', giveaway.title);
      text('[data-streamer-giveaway-quantity]', `${giveaway.quantity}×`);
      mount('[data-streamer-giveaway-image]', giveaway.image, { fit: 'contain', alt: giveaway.title });
    }
    if (promo) {
      this.querySelector('[data-streamer-experience-panel="promo"]').hidden = false;
      text('[data-streamer-promo-title]', promo.heading);
      text('[data-streamer-promo-description]', promo.description);
      mount('[data-streamer-promo-image]', promo.image);
      mount('[data-streamer-promo-icon]', promo.button_icon, { fit: 'contain', icon: true });
      const link = this.querySelector('[data-streamer-promo-action]');
      const href = safeLink(promo.cta_url);
      link.hidden = !href;
      if (href) link.href = href;
      if (promo.cta_label) link.setAttribute('aria-label', promo.cta_label);
      const promoClick = { experience_id: experience('promo').id, destination: href && destination(href) };
      link.addEventListener('click', () => this.track('banner.promo_clicked', promoClick), { signal: this._abortController.signal });
    }
    if (slider) {
      this._sliderExperienceId = experience('image_switcher').id;
      this.querySelector('[data-streamer-experience-panel="before-after"]').hidden = false;
      mount('[data-streamer-comparison-before]', before, { alt: slider.slides?.[0]?.label || '' });
      mount('[data-streamer-comparison-after]', after, { alt: slider.slides?.[1]?.label || '' });
      this._setComparisonPosition(50);
    }
    const shareImages = (slider ? [before, after] : [background])
      .map((image, index) => ({
        src: this._images.images[image?.shopify_file_gid]?.src,
        name: slider ? (index === 0 ? 'before' : 'after') : 'popup',
      }));
    if (shareImages.length === 2 && shareImages[0].src === shareImages[1].src) shareImages.shift();
    this._sharing = sharing ? new this._Sharing(this, shareImages, this._buildShareUrl(creator.referral_code), this._abortController.signal) : null;
    if (this._shareElement) this._shareElement.hidden = !sharing;
    return images;
  }

  _renderCTA(content, mount) {
    const original = this.querySelector('.streamer-popup__continue');
    original.hidden = !content.cta_label;
    original.querySelector('span').textContent = content.cta_label || '';
    mount('.streamer-popup__continue .madmonq-cta__icon', content.button_icon, { fit: 'contain', icon: true });
    const href = safeLink(content.cta_url);
    if (href) {
      const link = document.createElement('a');
      link.className = original.className;
      link.style.cssText = original.style.cssText;
      link.href = href;
      link.hidden = original.hidden;
      link.append(...original.childNodes);
      original.replaceWith(link);
      link.addEventListener('click', () => this._trackCta('link', href), { signal: this._abortController.signal });
    } else {
      original.addEventListener('click', () => {
        this._trackCta('continue');
        this.hide();
      }, { signal: this._abortController.signal });
    }
  }

  // The main button ends this view of the popup: "continue" closes it and a link leaves the page.
  _trackCta(action, href) {
    const seconds = secondsSince(this._view?.start);
    this.track('banner.cta_clicked', { action, destination: href && destination(href), seconds_open: seconds });
    this._endView('cta');
  }

  // Remembered only while the current event is handled; Escape does nothing until the focus trap is active.
  _closingWith(method) {
    this._closeMethod = method;
    window.setTimeout(() => { this._closeMethod = null; });
  }

  // Sends banner.closed once per view, with how the popup was closed.
  _endView(method) {
    if (!this.open || !this._view || this._view.closed) return;
    this._view.closed = true;
    this.track('banner.closed', { method, seconds_open: secondsSince(this._view.start) });
  }

  // Only failures before the popup was shown count; banner.failed has no layout.
  _trackFailure(error) {
    if (this._view || !this._banner) return;
    window.MadmonqAnalytics?.track('banner.failed', {
      campaign_id: this._banner.campaign_id,
      creator: this._banner.creator,
      reason: error instanceof PopupFailure ? error.reason : 'render_failed'
    });
  }

  _setComparisonPosition(value) {
    const position = Math.max(0, Math.min(100, Number(value) || 0));
    const canvas = 50 + position / 2;
    const panel = this.querySelector('[data-streamer-experience-panel="before-after"]');
    panel.style.setProperty('--streamer-comparison-position', `${canvas}%`);
    panel.style.setProperty('--streamer-comparison-clip-right', `${100 - canvas}%`);
    this.querySelector('[data-streamer-comparison-input]').value = String(position);
  }

  _buildShareUrl(code) {
    const url = new URL(window.location.origin);
    url.searchParams.set(this.dataset.referralParameter || 'ref', code);
    return url.href;
  }

  _setShareMenu(expanded) {
    if (!this._shareActions || !this._shareToggle) return;
    this._shareElement.toggleAttribute('data-expanded', expanded);
    this._shareToggle.setAttribute('aria-expanded', String(expanded));
    this._shareActions.setAttribute('aria-hidden', String(!expanded));
    this._shareActions.inert = !expanded;
    if (expanded) {
      this._sharing?.prepare();
      this.track('banner.share_opened');
    }
    if (!expanded && this._shareActions.contains(document.activeElement)) this._shareToggle.focus();
  }

  _waitForImages(images, signal) {
    return new Promise((resolve, reject) => {
      const finish = (error) => {
        window.clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
        error ? reject(error) : resolve();
      };
      const abort = () => finish(new DOMException('Popup disconnected', 'AbortError'));
      const timeout = window.setTimeout(() => finish(new DOMException('Campaign artwork took too long to load.', 'TimeoutError')), REQUEST_TIMEOUT);
      signal.addEventListener('abort', abort, { once: true });
      Promise.all(images.map((img) => img.decode())).then(() => finish(), finish);
    });
  }

  _showTransition(animate = true) {
    if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();
    return Promise.all([
      this.querySelector('.streamer-popup__backdrop').animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' }).finished,
      this.querySelector('.streamer-popup__stage').animate([
        { opacity: 0, transform: 'translateY(20px) scale(0.985)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' }
      ], { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }).finished
    ]).catch(() => {});
  }

  _hideTransition() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();
    this.setAttribute('data-closing', '');
    return Promise.all([
      this.querySelector('.streamer-popup__backdrop').animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: 'ease-in' }).finished,
      this.querySelector('.streamer-popup__stage').animate([
        { opacity: 1, transform: 'translateY(0) scale(1)' },
        { opacity: 0, transform: 'translateY(12px) scale(0.99)' }
      ], { duration: 180, easing: 'ease-in' }).finished
    ]).catch(() => {}).finally(() => this.removeAttribute('data-closing'));
  }

  _reportError(error) {
    if (window.Shopify?.designMode) console.warn('[MADMONQ Campaigns]', error);
  }
}

if (!customElements.get('streamer-popup')) customElements.define('streamer-popup', StreamerCampaignPopup);
